import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Link } from "react-router-dom";
import { Loader2, Users, DollarSign, Wallet, TrendingUp, AlertTriangle, Search, ExternalLink, ChevronLeft, ChevronRight, Sparkles, Zap, Crown, MessageSquare, Save, Check, Image as ImageIcon, RefreshCw, Mail, Plus as PlusIcon, X as XIcon, Flag, EyeOff, Eye, Trash2, Database } from "lucide-react";
import BankHealth from "@/components/admin/BankHealth";

const TABS = [
  { id: "overview", label: "Overview", icon: TrendingUp },
  { id: "users", label: "Users", icon: Users },
  { id: "ledger", label: "Credit Ledger", icon: Wallet },
  { id: "feedback", label: "User Feedback", icon: MessageSquare },
  { id: "community", label: "Community Reports", icon: Flag },
  { id: "audit", label: "Image Audit", icon: ImageIcon },
  { id: "finance", label: "Financials", icon: DollarSign },
  { id: "bank", label: "Passage Bank", icon: Database },
];

const PLAN_TINT = { free: "#6a7570", plus: "#4f7942", pro: "#c98a3b" };
const PLAN_ICON = { free: Sparkles, plus: Zap, pro: Crown };

const usd = (cents) => (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function StatCard({ label, value, sub, accent = "var(--primary)", testid }) {
  return (
    <div className="pm-card p-5" data-testid={testid} style={{ borderTop: `3px solid ${accent}` }}>
      <div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="font-display font-bold text-3xl" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{sub}</div>}
    </div>
  );
}

function TestEmailCard() {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, msg }

  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/admin/dashboard/test-email", { to: to.trim() || undefined });
      setResult({ ok: true, msg: `Sent from ${r.data.from} to ${r.data.to}. Check inbox + spam.` });
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      setResult({ ok: false, msg: detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pm-card p-5" data-testid="test-email-card">
      <div className="flex items-center gap-2 mb-2">
        <Mail size={16} style={{ color: "var(--primary)" }} />
        <div className="font-display font-bold text-lg">Resend · Send test email</div>
      </div>
      <div className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
        Verifies that <code>RESEND_API_KEY</code> + <code>RESEND_FROM_EMAIL</code> + DNS (SPF/DKIM) are configured. Leave blank to send to your own admin email.
      </div>
      <div className="flex gap-2 flex-wrap">
        <input
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="recipient@example.com (optional)"
          data-testid="test-email-to-input"
          className="flex-1 min-w-[220px] px-3 py-2 rounded border text-sm"
          style={{ borderColor: "var(--border)", background: "var(--card)" }}
        />
        <button
          onClick={send}
          disabled={busy}
          data-testid="test-email-send-btn"
          className="px-4 py-2 rounded font-semibold text-sm text-white disabled:opacity-60"
          style={{ background: "var(--primary)" }}
        >
          {busy ? <><Loader2 className="animate-spin inline mr-1" size={14} />Sending…</> : "Send test"}
        </button>
      </div>
      {result && (
        <div
          className="mt-3 text-sm px-3 py-2 rounded"
          data-testid={result.ok ? "test-email-success" : "test-email-error"}
          style={{
            background: result.ok ? "rgba(79,121,66,0.1)" : "rgba(200,80,80,0.1)",
            color: result.ok ? "#4f7942" : "#b04040",
            border: `1px solid ${result.ok ? "#4f7942" : "#b04040"}`,
          }}
        >
          {result.ok ? "✓ " : "✗ "}{result.msg}
        </div>
      )}
    </div>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="pm-card p-8 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>;
  const total = data.total_users || 1;
  const planPct = (n) => Math.round((n / total) * 100);
  return (
    <div className="space-y-5" data-testid="admin-overview">
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard testid="stat-total-users" label="Total users" value={data.total_users} sub={`+${data.new_users_30d} in the last 30 days`} />
        <StatCard testid="stat-new-7d" label="New (7d)" value={data.new_users_7d} accent="#4f7942" />
        <StatCard testid="stat-active-7d" label="Active users (7d)" value={data.active_users_7d} sub={`${data.active_users_30d} in last 30d`} accent="#3f7fbf" />
        <StatCard testid="stat-conversion" label="Free → Paid" value={`${data.conversion.rate_pct}%`} sub={`${data.conversion.ever_paid} paid signups of ${data.conversion.denominator}`} accent="#c98a3b" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="pm-card p-5" data-testid="plan-mix-card">
          <div className="font-display font-bold text-lg mb-3">Plan mix</div>
          {Object.entries(data.plans).map(([pid, count]) => {
            const Icon = PLAN_ICON[pid] || Sparkles;
            return (
              <div key={pid} className="mb-3" data-testid={`plan-row-${pid}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: PLAN_TINT[pid] }}>
                    <Icon size={14} />
                    <span className="uppercase tracking-wider">{pid}</span>
                  </div>
                  <div className="text-sm">
                    <b>{count}</b> <span style={{ color: "var(--text-muted)" }}>· {planPct(count)}%</span>
                  </div>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
                  <div className="h-full" style={{ width: `${planPct(count)}%`, background: PLAN_TINT[pid] }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="pm-card p-5" data-testid="subscription-health-card">
          <div className="font-display font-bold text-lg mb-3">Subscription health</div>
          {Object.entries(data.subscription_health).map(([k, v]) => {
            const label = { cancel_scheduled: "Cancel scheduled", canceled: "Canceled", past_due: "Past due", unpaid: "Unpaid" }[k] || k;
            const tone = v > 0 ? (k === "past_due" || k === "unpaid" ? "var(--weakness)" : "#c98a3b") : "var(--text-muted)";
            return (
              <div key={k} className="flex justify-between text-sm py-2 border-b" style={{ borderColor: "var(--border)" }} data-testid={`health-${k}`}>
                <span style={{ color: "var(--text-secondary)" }}>{label}</span>
                <b style={{ color: tone }}>{v}</b>
              </div>
            );
          })}
        </div>
      </div>
      <TestEmailCard />
    </div>
  );
}

function Users_() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const [loading, setLoading] = useState(true);
  const [grantUser, setGrantUser] = useState(null); // row being granted credits

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (plan) params.set("plan", plan);
    params.set("limit", limit); params.set("offset", offset);
    api.get(`/admin/dashboard/users?${params}`).then((r) => {
      setRows(r.data.rows); setTotal(r.data.total);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [plan, offset]);   // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="pm-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search size={14} style={{ color: "var(--text-muted)" }} />
          <input
            data-testid="user-search-input"
            className="pm-input flex-1"
            placeholder="Search email or name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setOffset(0); load(); } }}
          />
        </div>
        {["", "free", "plus", "pro"].map((p) => (
          <button
            key={p || "all"}
            onClick={() => { setPlan(p); setOffset(0); }}
            className="pm-badge"
            style={{
              background: plan === p ? "var(--primary)" : "var(--muted)",
              color: plan === p ? "white" : "var(--text)",
              cursor: "pointer",
              padding: "6px 14px",
            }}
            data-testid={`user-plan-filter-${p || "all"}`}
          >
            {p || "All"}
          </button>
        ))}
        <button
          onClick={() => load()}
          disabled={loading}
          className="pm-badge inline-flex items-center gap-1.5 disabled:opacity-50"
          style={{
            background: "var(--muted)",
            color: "var(--text)",
            cursor: loading ? "not-allowed" : "pointer",
            padding: "6px 14px",
          }}
          data-testid="users-refresh-btn"
          title="Reload user list"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>
      <div className="pm-card overflow-x-auto" data-testid="users-table-card">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="animate-spin inline" size={16} /></div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>No users match.</div>
        ) : (
          <table className="w-full text-sm" data-testid="users-table">
            <thead style={{ background: "var(--muted)" }}>
              <tr>
                {["User", "Plan", "Signup", "Last login", "Credits", "Topics", "Stripe", ""].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                // Email is always the stable primary ID; name is the friendly label.
                const friendly = (u.name && u.name.trim()) ? u.name : (u.email || "").split("@")[0];
                return (
                <tr key={u.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-sm" data-testid={`user-email-${u.id}`}>{u.email || "—"}</div>
                    <div className="text-xs" style={{ color: "var(--text-muted)" }} data-testid={`user-name-${u.id}`}>{friendly}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="pm-badge text-xs uppercase" style={{ background: `${PLAN_TINT[u.plan]}22`, color: PLAN_TINT[u.plan] }}>{u.plan}</span>
                    {u.subscription_status && u.subscription_status !== "active" && (
                      <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{u.subscription_status}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs" style={{ color: "var(--text-secondary)" }}>{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-2 text-xs" style={{ color: "var(--text-secondary)" }}>{fmtDT(u.last_login_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    <div><b>{u.credits_available ?? u.credits_remaining}</b> available</div>
                    <div style={{ color: "var(--text-muted)" }}>
                      {u.credits_remaining} plan + {u.credits_topup ?? 0} topup · {u.credits_used} used
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center font-semibold">{u.topics_generated}</td>
                  <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>
                    {u.stripe_customer_id ? (
                      <a href={`https://dashboard.stripe.com/test/customers/${u.stripe_customer_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">
                        {u.stripe_customer_id.slice(0, 14)}… <ExternalLink size={10} />
                      </a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => setGrantUser(u)}
                      className="pm-badge inline-flex items-center gap-1 text-xs hover:opacity-80"
                      style={{ background: "var(--hl-blue)", color: "var(--primary)" }}
                      data-testid={`grant-credits-btn-${u.id}`}
                      title={`Grant credits to ${u.email}`}
                    >
                      <PlusIcon size={11} /> Credits
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex items-center justify-between text-xs" style={{ color: "var(--text-muted)" }}>
        <div>{total} users total · showing {offset + 1}–{Math.min(offset + limit, total)}</div>
        <div className="flex gap-2">
          <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0} className="pm-badge inline-flex items-center gap-1 disabled:opacity-40" data-testid="users-prev-btn"><ChevronLeft size={12} /> Prev</button>
          <button onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total} className="pm-badge inline-flex items-center gap-1 disabled:opacity-40" data-testid="users-next-btn">Next <ChevronRight size={12} /></button>
        </div>
      </div>
      {grantUser && (
        <GrantCreditsModal
          user={grantUser}
          onClose={() => setGrantUser(null)}
          onGranted={() => { setGrantUser(null); load(); }}
        />
      )}
    </div>
  );
}

function GrantCreditsModal({ user, onClose, onGranted }) {
  const [credits, setCredits] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null); // { granted, new_balance_remaining }

  const quickPicks = [1, 5, 10, 25];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const n = parseFloat(credits);
    if (!(n > 0) || n > 1000) {
      setError("Enter a credit amount between 0.1 and 1000.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/admin/dashboard/users/${user.id}/grant-credits`, {
        credits: n,
        note: note.trim() || undefined,
      });
      setSuccess(r.data);
      setTimeout(onGranted, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Grant failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,37,71,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="grant-credits-modal"
    >
      <div className="pm-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display font-bold text-xl">Grant credits</div>
            <div className="text-sm mt-0.5" style={{ color: "var(--text-secondary)" }}>
              to <b>{user.email}</b>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--muted)]"
            aria-label="Close"
            data-testid="grant-close-btn"
          >
            <XIcon size={18} />
          </button>
        </div>

        {success ? (
          <div
            className="p-4 rounded-xl"
            style={{ background: "rgba(79,121,66,0.1)", border: "1px solid #4f7942" }}
            data-testid="grant-credits-success"
          >
            <div className="flex items-center gap-2 font-semibold" style={{ color: "#4f7942" }}>
              <Check size={16} /> Granted {success.granted} credits
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
              New balance: <b>{success.new_balance_remaining}</b> credits remaining
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="grant-credits-form">
            <div>
              <label className="text-sm font-medium block mb-1.5">Amount</label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                max="1000"
                className="pm-input"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                placeholder="e.g. 5"
                autoFocus
                data-testid="grant-credits-amount"
              />
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {quickPicks.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setCredits(String(n))}
                    className="pm-badge text-xs hover:opacity-80"
                    data-testid={`grant-quick-${n}`}
                  >
                    +{n}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">
                Note <span style={{ color: "var(--text-muted)" }}>(optional)</span>
              </label>
              <input
                type="text"
                maxLength={200}
                className="pm-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Beta tester bonus"
                data-testid="grant-credits-note"
              />
            </div>
            <div className="text-xs p-3 rounded-xl" style={{ background: "var(--muted)", color: "var(--text-secondary)" }}>
              Current balance: <b>{user.credits_available ?? user.credits_remaining}</b> available
              ({user.credits_remaining} plan + {user.credits_topup ?? 0} topup). Granted credits go to the topup bucket and never expire.
            </div>
            {error && (
              <div
                className="text-sm p-3 rounded-xl"
                style={{ background: "#fdecea", color: "var(--weakness)" }}
                data-testid="grant-credits-error"
              >
                {error}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={busy || !credits}
                className="pm-btn-primary flex items-center justify-center gap-2 flex-1"
                data-testid="grant-credits-submit"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                Grant credits
              </button>
              <button
                type="button"
                onClick={onClose}
                className="pm-btn-secondary"
                data-testid="grant-credits-cancel"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Ledger() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (reason) params.set("reason", reason);
    if (status) params.set("status", status);
    params.set("limit", limit); params.set("offset", offset);
    api.get(`/admin/dashboard/ledger?${params}`).then((r) => {
      setRows(r.data.rows); setTotal(r.data.total);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [reason, status, offset]);   // eslint-disable-line

  const STATUS_TONE = {
    succeeded: "#4f7942", refunded: "#3f7fbf",
    failed: "#c95e5e", granted: "#c98a3b",
  };

  return (
    <div className="space-y-4" data-testid="admin-ledger">
      <div className="pm-card p-4 flex flex-wrap gap-3 items-center" data-testid="ledger-filters">
        <span className="text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Status:</span>
        {["", "succeeded", "failed", "refunded", "granted"].map((s) => (
          <button key={s || "all"} onClick={() => { setStatus(s); setOffset(0); }} className="pm-badge" data-testid={`ledger-status-${s || "all"}`}
            style={{ background: status === s ? "var(--primary)" : "var(--muted)", color: status === s ? "white" : "var(--text)", cursor: "pointer", padding: "5px 12px" }}>
            {s || "All"}
          </button>
        ))}
        <span className="text-xs uppercase tracking-wider ml-3" style={{ color: "var(--text-muted)" }}>Reason:</span>
        {["", "topic_bundle", "coach_message", "question_pack", "credit_pack_topup", "signup_grant"].map((r) => (
          <button key={r || "all"} onClick={() => { setReason(r); setOffset(0); }} className="pm-badge text-xs" data-testid={`ledger-reason-${r || "all"}`}
            style={{ background: reason === r ? "var(--primary)" : "var(--muted)", color: reason === r ? "white" : "var(--text)", cursor: "pointer", padding: "5px 12px" }}>
            {r || "All"}
          </button>
        ))}
      </div>
      <div className="pm-card overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="animate-spin inline" size={16} /></div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>No ledger entries match.</div>
        ) : (
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead style={{ background: "var(--muted)" }}>
              <tr>
                {["When", "User", "Delta", "Reason", "Status", "Balance after", "Est. cost"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const tone = STATUS_TONE[r.status] || "var(--text-muted)";
                const deltaSign = r.delta > 0 ? "+" : "";
                return (
                  <tr key={i} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`ledger-row-${i}`}>
                    <td className="px-3 py-2 text-xs" style={{ color: "var(--text-muted)" }}>{fmtDT(r.at)}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-xs">{r.user_name || "—"}</div>
                      <div className="text-xs" style={{ color: "var(--text-muted)" }}>{r.user_email}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: r.delta > 0 ? "#4f7942" : "#c95e5e" }}>
                      {deltaSign}{r.delta}
                    </td>
                    <td className="px-3 py-2 text-xs">{r.reason}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.status ? <span className="pm-badge text-xs uppercase" style={{ background: `${tone}22`, color: tone }}>{r.status}</span> : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: "var(--text-secondary)" }}>{r.balance_after ?? "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: "var(--text-muted)" }}>
                      {r.cost_cents ? `$${(r.cost_cents / 100).toFixed(4)}` : r.revenue_cents ? <span style={{ color: "#4f7942" }}>+{usd(r.revenue_cents)}</span> : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex items-center justify-between text-xs" style={{ color: "var(--text-muted)" }}>
        <div>{total} entries · showing {offset + 1}–{Math.min(offset + limit, total)}</div>
        <div className="flex gap-2">
          <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0} className="pm-badge inline-flex items-center gap-1 disabled:opacity-40"><ChevronLeft size={12} /> Prev</button>
          <button onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total} className="pm-badge inline-flex items-center gap-1 disabled:opacity-40">Next <ChevronRight size={12} /></button>
        </div>
      </div>
    </div>
  );
}

// Human-friendly labels for `credit_ledger.reason` values on the Financials
// breakdown panel. Anything not in this map falls back to a titleized version.
const REASON_LABELS = {
  topic_bundle: "Topic bundle (analogy + story + image)",
  coach_message: "Coach chat message",
  question_pack: "AI question pack",
  subtopic_analogy: "Subtopic analogy rewrite",
  pathway_image: "Pathway diagram image",
  image_regenerate: "Image regenerate",
  eli5_generation: "ELI5 rewrite",
  coach_message_claude_fail: "Coach failure (refunded)",
};

function CostByReasonPanel({ byReason, totalUsd }) {
  if (!byReason || byReason.length === 0) {
    return (
      <div className="pm-card p-5" data-testid="cost-by-reason-empty">
        <div className="font-display font-bold text-lg mb-1">Where's the money going?</div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
          No AI spend recorded this month yet. As soon as users generate topics, images, or coach messages this month, they'll appear here ranked by cost.
        </div>
      </div>
    );
  }
  const topPct = byReason[0]?.pct_of_spend || 0;
  return (
    <div className="pm-card p-5" data-testid="cost-by-reason-card">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-display font-bold text-lg">Where's the money going?</div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            Estimated AI spend this month by action type — sorted by highest burn.
            Tune the biggest driver in <b>Assumed cost per action</b> below.
          </div>
        </div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>
          Total: <b style={{ color: "var(--text)" }}>${totalUsd}</b>
        </div>
      </div>
      <div className="space-y-2" data-testid="cost-by-reason-rows">
        {byReason.map((row, idx) => {
          const label = REASON_LABELS[row.reason] || row.reason.replace(/_/g, " ");
          const barWidth = topPct > 0 ? (row.pct_of_spend / topPct) * 100 : 0;
          // Warm palette — top burner most saturated, tapering off.
          const tones = ["#c95e5e", "#c98a3b", "#e0a944", "#8b6f47", "#5e7a8b"];
          const tone = tones[Math.min(idx, tones.length - 1)];
          return (
            <div key={row.reason} data-testid={`cost-reason-${row.reason}`}>
              <div className="flex items-center justify-between text-sm mb-1">
                <div className="flex-1 min-w-0 pr-2 truncate">
                  <span className="font-semibold" style={{ color: tone }}>{label}</span>
                  <span className="text-xs ml-2" style={{ color: "var(--text-muted)" }}>
                    · {row.count.toLocaleString()} calls
                  </span>
                </div>
                <div className="text-right whitespace-nowrap">
                  <b>${row.cost_usd.toFixed(4)}</b>
                  <span className="text-xs ml-2" style={{ color: "var(--text-muted)" }}>
                    {row.pct_of_spend}%
                  </span>
                </div>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
                <div className="h-full transition-all" style={{ width: `${Math.max(2, barWidth)}%`, background: tone }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Finance() {
  const [data, setData] = useState(null);
  const [costDraft, setCostDraft] = useState({});
  const [savingCost, setSavingCost] = useState(false);
  const [savedTag, setSavedTag] = useState(false);

  const reload = () => api.get("/admin/dashboard/finance").then((r) => {
    setData(r.data);
    setCostDraft(r.data.cost_config || {});
  });
  useEffect(() => { reload(); }, []);
  if (!data) return <div className="pm-card p-8 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>;
  const activeAlert = data.ai_spend.alerts.filter((a) => a.hit).slice(-1)[0];
  const pct = Math.min(100, data.ai_spend.pct_of_budget);
  const barTone = pct >= 100 ? "#c95e5e" : pct >= 90 ? "#c98a3b" : pct >= 70 ? "#e0a944" : "#4f7942";

  const saveCosts = async () => {
    setSavingCost(true);
    try {
      await api.put("/admin/dashboard/cost-config", { values: costDraft });
      setSavedTag(true);
      setTimeout(() => setSavedTag(false), 2500);
      reload();
    } finally { setSavingCost(false); }
  };

  const changed = data.cost_config && Object.keys(costDraft).some((k) => Number(costDraft[k]) !== Number(data.cost_config[k]));

  return (
    <div className="space-y-5" data-testid="admin-finance">
      <div className="grid md:grid-cols-4 gap-4">
        <StatCard testid="stat-mrr" label="MRR" value={usd(data.mrr.cents)} sub={`${data.mrr.by_plan.plus.count} Plus · ${data.mrr.by_plan.pro.count} Pro`} accent="#4f7942" />
        <StatCard testid="stat-topup" label="Top-ups this month" value={usd(data.topup_revenue.cents)} sub={`${data.topup_revenue.purchase_count} purchases`} accent="#c98a3b" />
        <StatCard testid="stat-total-revenue" label="Total revenue (MTD)" value={usd(data.total_revenue_cents)} accent="#3f7fbf" />
        <StatCard testid="stat-gross-profit" label="Gross profit (MTD)" value={usd(data.gross_profit.cents)} sub={`Avg cost / user: $${data.avg_cost_per_active_user_usd}`} accent={data.gross_profit.cents >= 0 ? "#4f7942" : "#c95e5e"} />
      </div>

      {/* Budget bar */}
      <div className="pm-card p-5" data-testid="budget-card" style={{ borderLeft: activeAlert ? `4px solid ${barTone}` : undefined }}>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <div className="font-display font-bold text-lg">AI spend vs monthly budget</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>Estimated cost per action tracked in the credit ledger.</div>
          </div>
          <div className="text-right">
            <div className="font-display font-bold text-2xl" style={{ color: barTone }}>{usd(data.ai_spend.cents)}</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>of {usd(data.ai_spend.budget_cents)} ({data.ai_spend.pct_of_budget}%)</div>
          </div>
        </div>
        <div className="h-3 rounded-full overflow-hidden relative" style={{ background: "var(--muted)" }}>
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: barTone }} />
          {[70, 90, 100].map((t) => (
            <div key={t} className="absolute top-0 bottom-0" style={{ left: `${t}%`, width: 2, background: "rgba(0,0,0,0.15)" }} />
          ))}
        </div>
        <div className="flex justify-between text-xs mt-1" style={{ color: "var(--text-muted)" }}>
          <span>0</span><span>70% ${(data.ai_spend.budget_cents * 0.7 / 100).toFixed(0)}</span><span>90%</span><span>100% ${(data.ai_spend.budget_cents / 100).toFixed(0)}</span>
        </div>
        {activeAlert && (
          <div className="mt-3 pm-badge inline-flex items-center gap-1.5 text-xs" style={{ background: `${barTone}22`, color: barTone }} data-testid="budget-alert">
            <AlertTriangle size={12} /> {activeAlert.threshold_pct}% of budget reached
          </div>
        )}
      </div>

      {/* Cost breakdown by feature/reason */}
      <CostByReasonPanel byReason={data.ai_spend.by_reason || []} totalUsd={data.ai_spend.usd} />

      {/* Per-plan credit consumption */}
      <div className="pm-card p-5" data-testid="avg-credits-card">
        <div className="font-display font-bold text-lg mb-3">Avg credits used per plan (this month)</div>
        {Object.entries(data.avg_credits_used_by_plan).map(([pid, avg]) => (
          <div key={pid} className="flex justify-between items-center text-sm py-2 border-b" style={{ borderColor: "var(--border)" }} data-testid={`avg-plan-${pid}`}>
            <span className="uppercase font-semibold" style={{ color: PLAN_TINT[pid] || "var(--text)" }}>{pid}</span>
            <b>{avg}</b>
          </div>
        ))}
      </div>

      {/* Editable cost config */}
      <div className="pm-card p-5" data-testid="cost-config-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="font-display font-bold text-lg">Assumed cost per action</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>
              Values in <b>cents</b>. New ledger entries use the saved values; historical rows keep their original cost.
            </div>
          </div>
          <button
            onClick={saveCosts}
            disabled={savingCost || !changed}
            className="pm-btn-primary inline-flex items-center gap-2 text-sm"
            style={{ opacity: !changed ? 0.5 : 1 }}
            data-testid="cost-config-save"
          >
            {savingCost ? <Loader2 size={14} className="animate-spin" /> : savedTag ? <Check size={14} /> : <Save size={14} />}
            {savingCost ? "Saving…" : savedTag ? "Saved" : "Save changes"}
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {Object.entries(costDraft).map(([k, cents]) => {
            const def = data.cost_config?.[k];
            return (
              <div key={k} className="flex justify-between items-center border-b py-2" style={{ borderColor: "var(--border)" }}>
                <div>
                  <div style={{ color: "var(--text-secondary)" }}>{k.replace(/_/g, " ")}</div>
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>Current: ${(def / 100).toFixed(4)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>¢</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cents}
                    onChange={(e) => setCostDraft({ ...costDraft, [k]: Number(e.target.value) })}
                    className="pm-input w-24 text-right font-mono"
                    data-testid={`cost-input-${k}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const FEEDBACK_CATEGORY_TINT = { content: "#4f7942", bug: "#c95e5e", suggestion: "#c98a3b", other: "#6a7570" };

function UserFeedback() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [category, setCategory] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 30;
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (category) p.set("category", category);
    p.set("limit", limit); p.set("offset", offset);
    api.get(`/admin/user-feedback?${p}`).then((r) => {
      setRows(r.data.rows); setTotal(r.data.total); setCounts(r.data.counts);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [category, offset]);  // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="admin-user-feedback">
      <div className="pm-card p-4 flex flex-wrap items-center gap-3" data-testid="feedback-filters">
        <span className="text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Category:</span>
        {[
          { id: "", label: `All (${total})` },
          ...["content", "bug", "suggestion", "other"].map((c) => ({ id: c, label: `${c} (${counts[c] || 0})` })),
        ].map((c) => (
          <button
            key={c.id || "all"}
            onClick={() => { setCategory(c.id); setOffset(0); }}
            className="pm-badge capitalize"
            data-testid={`feedback-cat-${c.id || "all"}`}
            style={{
              background: category === c.id ? "var(--primary)" : "var(--muted)",
              color: category === c.id ? "white" : "var(--text)",
              cursor: "pointer", padding: "6px 14px",
            }}
          >
            {c.label}
          </button>
        ))}
      </div>
      <div className="pm-card">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="animate-spin inline" size={16} /></div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>No feedback yet in this bucket.</div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--border)" }}>
            {rows.map((r) => {
              const tone = FEEDBACK_CATEGORY_TINT[r.category] || "var(--text-muted)";
              return (
                <div key={r.id} className="p-4" data-testid={`feedback-row-${r.id}`}>
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="pm-badge text-xs uppercase" style={{ background: `${tone}22`, color: tone }}>{r.category}</span>
                    <div className="text-sm font-semibold">{r.user_name || r.user_email || "Unknown user"}</div>
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>{r.user_email}</div>
                    {r.user_plan && (
                      <span className="pm-badge text-xs uppercase" style={{ background: `${PLAN_TINT[r.user_plan]}22`, color: PLAN_TINT[r.user_plan] }}>{r.user_plan}</span>
                    )}
                    <div className="text-xs ml-auto" style={{ color: "var(--text-muted)" }}>{fmtDT(r.created_at)}</div>
                  </div>
                  <div className="text-sm mb-1" style={{ color: "var(--text-secondary)" }}>{r.message}</div>
                  {r.page && (
                    <div className="text-xs" style={{ color: "var(--text-muted)" }}>Page: <code>{r.page}</code></div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between text-xs" style={{ color: "var(--text-muted)" }}>
        <div>{total} total · showing {offset + 1}–{Math.min(offset + limit, total)}</div>
        <div className="flex gap-2">
          <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0} className="pm-badge inline-flex items-center gap-1 disabled:opacity-40" data-testid="feedback-prev-btn"><ChevronLeft size={12} /> Prev</button>
          <button onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total} className="pm-badge inline-flex items-center gap-1 disabled:opacity-40" data-testid="feedback-next-btn">Next <ChevronRight size={12} /></button>
        </div>
      </div>
    </div>
  );
}

function ImageAudit() {
  const [status, setStatus] = useState(null);
  const [flagged, setFlagged] = useState([]);
  const [starting, setStarting] = useState(false);
  const [regenerating, setRegenerating] = useState({});
  const pollRef = useState({ current: null })[0];

  const refresh = async () => {
    try {
      const s = await api.get("/admin/audit/images/status");
      setStatus(s.data);
    } catch { /* silent */ }
    try {
      const f = await api.get("/admin/audit/images/flagged");
      setFlagged(f.data.rows || []);
    } catch { /* silent */ }
  };
  useEffect(() => {
    refresh();
    // Poll every 3s while a scan is running
    const t = setInterval(() => {
      if (status?.scan?.status === "running") refresh();
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.scan?.status]);

  const startScan = async () => {
    setStarting(true);
    try {
      await api.post("/admin/audit/images/start");
      await refresh();
    } finally { setStarting(false); }
  };

  const regenTopic = async (topic_id) => {
    setRegenerating((r) => ({ ...r, [topic_id]: true }));
    try {
      await api.post(`/admin/audit/images/regen-topic/${topic_id}`);
      await refresh();
    } finally { setRegenerating((r) => ({ ...r, [topic_id]: false })); }
  };

  const s = status?.scan || {};
  const pct = s.total ? Math.min(100, Math.round((s.scanned / s.total) * 100)) : 0;

  return (
    <div className="space-y-5" data-testid="admin-audit">
      <div className="pm-card p-5" data-testid="audit-scan-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div>
            <div className="font-display font-bold text-lg">Image quality audit</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>
              Claude Vision scans every generated image for garbled text or letters (a known Gemini quirk). Flagged images can be regenerated with a stricter "no text" prompt.
            </div>
          </div>
          <button
            onClick={startScan}
            disabled={starting || s.status === "running"}
            className="pm-btn-primary inline-flex items-center gap-2 text-sm"
            data-testid="audit-start-btn"
          >
            {starting || s.status === "running" ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
            {s.status === "running" ? "Scanning…" : starting ? "Starting…" : "Start / re-run scan"}
          </button>
        </div>
        {s.status && s.status !== "idle" && (
          <>
            <div className="grid md:grid-cols-4 gap-3 mb-3">
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>Status: <b style={{ color: "var(--text)" }}>{s.status}</b></div>
              <div className="text-xs">Scanned: <b>{s.scanned}/{s.total}</b></div>
              <div className="text-xs">Flagged: <b style={{ color: "#c95e5e" }}>{s.flagged || 0}</b></div>
              <div className="text-xs">Errors: <b style={{ color: s.errors ? "#c98a3b" : "var(--text-muted)" }}>{s.errors || 0}</b></div>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--muted)" }}>
              <div className="h-full transition-all" style={{ width: `${pct}%`, background: "var(--primary)" }} />
            </div>
          </>
        )}
        {status?.flagged_total !== undefined && (
          <div className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}>
            <b style={{ color: status.flagged_total > 0 ? "#c95e5e" : "#4f7942" }}>{status.flagged_total}</b> image(s) currently flagged for regeneration.
          </div>
        )}
      </div>

      {flagged.length > 0 && (
        <div className="pm-card p-5" data-testid="audit-flagged-list">
          <div className="font-display font-bold text-lg mb-3">Flagged images ({flagged.length})</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {flagged.map((row) => (
              <div key={`${row._kind}-${row.id}`} className="pm-card p-3 flex flex-col" data-testid={`flagged-${row.id}`}>
                <div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
                  {row._kind} · {row.text_audit?.severity || "unknown"}
                </div>
                <div className="font-semibold text-sm mb-2 truncate" title={row.name || row.prompt}>
                  {row.name || row.prompt}
                </div>
                {row.image_url && (
                  <img
                    src={row.image_url}
                    alt={row.name || "flagged"}
                    className="w-full h-32 object-cover rounded-md mb-2"
                    style={{ background: "var(--muted)" }}
                  />
                )}
                {row.text_audit?.text_visible && (
                  <div className="text-xs mb-2 italic p-2 rounded" style={{ background: "#c95e5e10", color: "#c95e5e" }}>
                    "{row.text_audit.text_visible.slice(0, 90)}"
                  </div>
                )}
                {row._kind === "topic" && (
                  <button
                    onClick={() => regenTopic(row.id)}
                    disabled={regenerating[row.id]}
                    className="pm-btn-primary text-xs inline-flex items-center justify-center gap-1 mt-auto"
                    data-testid={`regen-btn-${row.id}`}
                  >
                    {regenerating[row.id] ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    {regenerating[row.id] ? "Regenerating…" : "Regenerate"}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BudgetBanner({ setTab }) {
  const [spend, setSpend] = useState(null); // { usd, budget_usd, pct_of_budget }
  const [cfg, setCfg] = useState(null); // { monthly_budget_cents, alert_thresholds }
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const [fin, bud] = await Promise.all([
      api.get("/admin/dashboard/finance"),
      api.get("/admin/dashboard/budget-config"),
    ]);
    setSpend(fin.data.ai_spend);
    setCfg(bud.data);
  };

  useEffect(() => {
    reload().catch(() => {});
  }, []);

  const save = async () => {
    const dollars = parseFloat(draft);
    if (isNaN(dollars) || dollars <= 0) return;
    setSaving(true);
    try {
      await api.put("/admin/dashboard/budget-config", {
        monthly_budget_cents: Math.round(dollars * 100),
      });
      await reload();
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!spend || !cfg) return null;

  const pct = Math.min(999, spend.pct_of_budget || 0);
  // Tone escalates with % used. Green under 50%, then yellow, orange, red.
  const tone =
    pct >= 100 ? "#c95e5e"
    : pct >= 90 ? "#d97706"
    : pct >= 70 ? "#e0a944"
    : pct >= 50 ? "#c98a3b"
    : "#4f7942";
  const barPct = Math.min(100, pct);
  const isWarn = pct >= 70;

  return (
    <div
      className="pm-card p-4 mb-5"
      style={{ borderLeft: `4px solid ${tone}`, background: `${tone}0f` }}
      data-testid="budget-banner"
    >
      <div className="flex items-start gap-3 mb-3">
        <AlertTriangle size={20} style={{ color: tone, flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-sm" style={{ color: tone }}>
              Universal LLM Key — Budget Monitor
            </span>
            {isWarn && (
              <span
                className="pm-badge text-[10px]"
                style={{ background: tone, color: "white", padding: "2px 8px" }}
                data-testid="budget-banner-warning-chip"
              >
                {pct >= 100 ? "OVER BUDGET" : pct >= 90 ? "CRITICAL" : "APPROACHING CAP"}
              </span>
            )}
          </div>
          <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
            Estimated AI spend this month based on your ledger costs. Universal Key balance is inferred — top up in Emergent → Profile → Manage plan.
          </div>
        </div>
        {!editing && (
          <button
            onClick={() => {
              setDraft(String(cfg.monthly_budget_usd));
              setEditing(true);
            }}
            className="pm-badge text-xs whitespace-nowrap"
            style={{ background: "var(--muted)", color: "var(--text)", cursor: "pointer", padding: "6px 12px" }}
            data-testid="budget-banner-edit"
          >
            Edit budget
          </button>
        )}
      </div>

      {/* Spend row */}
      <div className="flex items-baseline gap-2 mb-2 flex-wrap">
        <span className="font-display font-bold text-2xl" style={{ color: tone }} data-testid="budget-banner-spend">
          ${spend.usd?.toLocaleString?.() ?? spend.usd}
        </span>
        <span className="text-sm" style={{ color: "var(--text-muted)" }}>
          of ${cfg.monthly_budget_usd.toLocaleString()} monthly cap
        </span>
        <span className="text-xs ml-auto font-bold" style={{ color: tone }} data-testid="budget-banner-pct">
          {pct.toFixed(1)}%
        </span>
      </div>

      {/* Progress bar with threshold ticks */}
      <div
        className="h-2 rounded-full overflow-hidden relative"
        style={{ background: "var(--muted)" }}
        data-testid="budget-banner-bar"
      >
        <div
          className="h-full transition-all"
          style={{ width: `${barPct}%`, background: tone }}
        />
        {cfg.alert_thresholds.map((t) => (
          <div
            key={t}
            className="absolute top-0 h-full w-px"
            style={{ left: `${t * 100}%`, background: "rgba(0,0,0,0.25)" }}
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>
        {cfg.alert_thresholds.map((t) => (
          <span key={t}>{Math.round(t * 100)}%</span>
        ))}
      </div>

      {/* Inline edit form */}
      {editing && (
        <div className="mt-3 flex gap-2 items-center flex-wrap" data-testid="budget-banner-edit-form">
          <span className="text-sm" style={{ color: "var(--text-secondary)" }}>Monthly cap: $</span>
          <input
            type="number"
            min="1"
            step="1"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="pm-input"
            style={{ width: 120, padding: "6px 10px" }}
            data-testid="budget-banner-input"
          />
          <button
            onClick={save}
            disabled={saving}
            className="pm-btn-primary text-xs inline-flex items-center gap-1"
            data-testid="budget-banner-save"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="text-xs"
            style={{ color: "var(--text-muted)" }}
            data-testid="budget-banner-cancel"
          >
            Cancel
          </button>
        </div>
      )}

      {isWarn && (
        <div className="mt-3 flex items-center justify-between gap-2 text-xs" style={{ color: "var(--text-secondary)" }}>
          <span>
            {pct >= 100
              ? "You've exceeded your monthly cap. New AI-heavy actions may surprise-fail if the Universal Key runs dry."
              : pct >= 90
              ? "You're within 10% of the cap. Consider topping up the Universal Key or raising the budget."
              : "Watch spend closely — you've crossed the 70% alert threshold."}
          </span>
          <button
            onClick={() => setTab("finance")}
            className="pm-badge text-xs whitespace-nowrap"
            style={{ background: tone, color: "white", cursor: "pointer", padding: "6px 14px" }}
            data-testid="budget-banner-cta"
          >
            View financials →
          </button>
        </div>
      )}
    </div>
  );
}

function CommunityReports() {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // report id being acted on

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/community-board/reports", { params: { status, limit: 100 } });
      setRows(r.data.reports || []);
      setOpenCount(r.data.open_count || 0);
      setTotal(r.data.total || 0);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (fn, report) => {
    setActing(report.id);
    try {
      await fn();
      await load();
    } finally { setActing(null); }
  };

  const resolveReport = (r) => act(async () => {
    await api.post(`/admin/community-board/reports/${r.id}/resolve`);
  }, r);
  const hideTarget = (r) => act(async () => {
    const path = r.target_type === "post"
      ? `/admin/community-board/posts/${r.target_id}/hide`
      : `/admin/community-board/replies/${r.target_id}/hide`;
    await api.post(path);
  }, r);
  const unhideTarget = (r) => act(async () => {
    const path = r.target_type === "post"
      ? `/admin/community-board/posts/${r.target_id}/unhide`
      : `/admin/community-board/replies/${r.target_id}/unhide`;
    await api.post(path);
  }, r);
  const deleteTarget = (r) => {
    if (!window.confirm(`Permanently delete this ${r.target_type}? This cannot be undone.`)) return;
    return act(async () => {
      const path = r.target_type === "post"
        ? `/admin/community-board/posts/${r.target_id}`
        : `/admin/community-board/replies/${r.target_id}`;
      await api.delete(path);
    }, r);
  };

  const imgSrc = (urlPath) => {
    if (!urlPath) return null;
    const token = localStorage.getItem("pm_token") || "";
    return `${API}${urlPath}?auth=${encodeURIComponent(token)}`;
  };

  return (
    <div className="space-y-4" data-testid="admin-community-reports">
      <div className="pm-card p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Flag size={14} style={{ color: "#c95e5e" }} />
          <strong className="text-sm">Community moderation queue</strong>
          <span className="pm-badge" style={{ background: "#c95e5e22", color: "#c95e5e", padding: "4px 10px" }} data-testid="admin-community-open-count">
            {openCount} open
          </span>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {[
            { id: "open", label: "Open" },
            { id: "resolved", label: "Resolved" },
            { id: "all", label: "All" },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              className="pm-badge"
              style={{
                background: status === s.id ? "var(--primary)" : "var(--muted)",
                color: status === s.id ? "white" : "var(--text)",
                padding: "6px 14px",
                cursor: "pointer",
              }}
              data-testid={`admin-community-filter-${s.id}`}
            >
              {s.label}
            </button>
          ))}
          <button
            onClick={load}
            disabled={loading}
            className="pm-badge inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "var(--muted)", color: "var(--text)", padding: "6px 14px", cursor: "pointer" }}
            data-testid="admin-community-refresh-btn"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="pm-card p-10 text-center"><Loader2 className="inline animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="pm-card p-10 text-center" data-testid="admin-community-empty">
          <Flag size={32} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <div className="font-display font-bold text-lg mb-1">Nothing to review</div>
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>No {status === "all" ? "" : status} reports.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const t = r.target || {};
            const working = acting === r.id;
            return (
              <div key={r.id} className="pm-card p-5" data-testid={`admin-report-row-${r.id}`}>
                <div className="flex items-start gap-3 flex-wrap">
                  <span
                    className="pm-badge"
                    style={{
                      background: r.target_type === "post" ? "#3f7fbf22" : "#4f794222",
                      color: r.target_type === "post" ? "#3f7fbf" : "#4f7942",
                      padding: "4px 10px",
                    }}
                  >
                    {r.target_type === "post" ? "Post" : "Reply"}
                  </span>
                  {r.target_hidden && (
                    <span className="pm-badge" style={{ background: "#c95e5e22", color: "#c95e5e", padding: "4px 10px" }}>
                      <EyeOff size={11} className="inline mr-1" /> Hidden
                    </span>
                  )}
                  {r.resolved && (
                    <span className="pm-badge" style={{ background: "#4f794222", color: "#4f7942", padding: "4px 10px" }}>
                      <Check size={11} className="inline mr-1" /> Resolved
                    </span>
                  )}
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                    Reported by <strong>{r.reporter_name || r.reporter_email || "unknown"}</strong>
                    · {new Date(r.created_at).toLocaleString()}
                  </span>
                </div>
                {r.reason && (
                  <div className="mt-2 text-sm italic" style={{ color: "var(--text-secondary)" }}>
                    “{r.reason}”
                  </div>
                )}
                {r.target_exists ? (
                  <div className="mt-3 p-3 rounded-lg" style={{ background: "var(--muted)" }}>
                    {t.title && <div className="font-display font-bold text-base mb-1">{t.title}</div>}
                    {t.image_url && (
                      <div className="mb-2 rounded-md overflow-hidden">
                        <img src={imgSrc(t.image_url)} alt="" className="max-h-40 object-contain" />
                      </div>
                    )}
                    {t.body && (
                      <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>
                        {t.body.length > 400 ? t.body.slice(0, 400) + "…" : t.body}
                      </div>
                    )}
                    <div className="text-[11px] mt-2" style={{ color: "var(--text-muted)" }}>
                      by {t.author_name || "?"} · {new Date(t.created_at).toLocaleString()}
                      {typeof t.report_count === "number" && ` · ${t.report_count} total reports`}
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-xs italic" style={{ color: "var(--text-muted)" }}>
                    (Target has been deleted)
                  </div>
                )}
                {!r.resolved && r.target_exists && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => resolveReport(r)}
                      disabled={working}
                      className="pm-btn-secondary inline-flex items-center gap-1 text-xs"
                      data-testid={`admin-report-resolve-${r.id}`}
                    >
                      <Check size={12} /> Dismiss report
                    </button>
                    {t.is_hidden ? (
                      <button
                        onClick={() => unhideTarget(r)}
                        disabled={working}
                        className="pm-btn-secondary inline-flex items-center gap-1 text-xs"
                        data-testid={`admin-report-unhide-${r.id}`}
                      >
                        <Eye size={12} /> Unhide
                      </button>
                    ) : (
                      <button
                        onClick={() => hideTarget(r)}
                        disabled={working}
                        className="pm-btn-primary inline-flex items-center gap-1 text-xs"
                        style={{ background: "#c98a3b", borderColor: "#c98a3b" }}
                        data-testid={`admin-report-hide-${r.id}`}
                      >
                        <EyeOff size={12} /> Hide
                      </button>
                    )}
                    <button
                      onClick={() => deleteTarget(r)}
                      disabled={working}
                      className="pm-btn-primary inline-flex items-center gap-1 text-xs"
                      style={{ background: "#c95e5e", borderColor: "#c95e5e" }}
                      data-testid={`admin-report-delete-${r.id}`}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                    {working && <Loader2 size={12} className="animate-spin" />}
                  </div>
                )}
              </div>
            );
          })}
          <div className="text-xs text-center" style={{ color: "var(--text-muted)" }}>
            Showing {rows.length} of {total} reports
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-dashboard-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-bold mb-1">Admin dashboard</h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Founder-only view of users, credits, and finances.</p>
        </div>
        <div className="flex gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
          <Link to="/app/admin/feedback" className="hover:underline">Question feedback →</Link>
          <Link to="/app/admin/playground" className="hover:underline">Playground analytics →</Link>
        </div>
      </div>

      {/* Persistent budget alert (visible on every tab) */}
      <BudgetBanner setTab={setTab} />

      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b overflow-x-auto" style={{ borderColor: "var(--border)" }} data-testid="admin-tabs">
        {TABS.map((t) => {
          const I = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-all whitespace-nowrap"
              style={{
                borderBottom: `3px solid ${active ? "var(--primary)" : "transparent"}`,
                color: active ? "var(--primary)" : "var(--text-secondary)",
                marginBottom: -1,
              }}
            >
              <I size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "users" && <Users_ />}
      {tab === "ledger" && <Ledger />}
      {tab === "feedback" && <UserFeedback />}
      {tab === "community" && <CommunityReports />}
      {tab === "audit" && <ImageAudit />}
      {tab === "finance" && <Finance />}
      {tab === "bank" && <BankHealth />}
    </div>
  );
}
